import { CollapsibleFilters } from "../../components/common/CollapsibleFilters";
import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  Card,
  Typography,
  Select,
  Option,
  Alert,
  Drawer,
  Sheet,
  Chip,
  Input,
  IconButton,
  Dropdown,
  MenuButton,
  Menu,
  MenuItem,
  Modal,
  ModalDialog,
  ModalClose,
  DialogTitle,
  DialogContent,
  Stack,
  FormControl,
  FormLabel,
  Textarea,
  Breadcrumbs,
  Link,
} from "@mui/joy";
import { useParams, useNavigate } from "react-router-dom";
import {
  ArrowLeft as BackIcon,
  MagnifyingGlass as SearchIcon,
  DownloadSimple as DownloadIcon,
  DotsThreeVertical as MoreIcon,
  CheckCircle as ProcessedIcon,
  Trash as DeleteIcon,
  Eye as ViewIcon,
  House as HomeIcon,
  CaretRight as ChevronRightIcon,
  Briefcase as OpportunityIcon,
} from "@phosphor-icons/react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import toast from "react-hot-toast";
import { format } from "date-fns";
import {
  ResponsiveTable,
  type ResponsiveColumn,
} from "../../components/common/ResponsiveTable";
import {
  formsApi,
  downloadSubmissionsCSV,
  formSubmissionsApi,
  type FormSubmission,
} from "../../lib/apiMarketing";
import OpportunityModal from "../../components/crm/opportunities/OpportunityModal";

export const FormSubmissions: React.FC = () => {
  const { formId } = useParams<{ formId: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [searchQuery, setSearchQuery] = useState("");
  const [page, setPage] = useState(1);
  const [processed, setProcessed] = useState("all");
  useEffect(() => {
    setPage(1);
  }, [searchQuery, processed, formId]);
  const [selectedSubmission, setSelectedSubmission] =
    useState<FormSubmission | null>(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [showProcessModal, setShowProcessModal] = useState(false);
  const [processingNotes, setProcessingNotes] = useState("");
  // Contact whose opportunity is being created from a submission. The modal
  // seeds itself from the contact, which now carries the address the
  // homeowner typed into the form.
  const [opportunityContactId, setOpportunityContactId] = useState<
    number | null
  >(null);

  // Fetch form details
  const {
    data: formData,
    isError: formError,
    refetch: reloadForm,
  } = useQuery({
    queryKey: ["form", formId],
    queryFn: () => formsApi.get(formId!),
    enabled: !!formId,
  });

  // Fetch submissions
  const {
    data: submissionsData,
    isLoading,
    isError,
    refetch,
  } = useQuery({
    queryKey: ["form-submissions", formId, page, searchQuery, processed],
    queryFn: () =>
      formsApi.getSubmissions(formId!, {
        page,
        search: searchQuery,
        processed,
      }),
    enabled: !!formId,
  });

  // Mark as processed mutation
  const markProcessedMutation = useMutation({
    mutationFn: ({ id, notes }: { id: string; notes?: string }) =>
      formSubmissionsApi.markProcessed(id, notes),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["form-submissions", formId] });
      setShowProcessModal(false);
      setProcessingNotes("");
      toast.success("Submission marked as processed");
    },
    onError: () => {
      toast.error("Failed to mark submission as processed");
    },
  });

  // Delete submission mutation
  const deleteSubmissionMutation = useMutation({
    mutationFn: (id: string) => formSubmissionsApi.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["form-submissions", formId] });
      toast.success("Submission deleted");
    },
    onError: () => {
      toast.error("Failed to delete submission");
    },
  });

  const form = formData?.data;
  const submissions = submissionsData?.data?.results || [];

  const [exporting, setExporting] = useState(false);
  const handleExportCSV = async () => {
    if (!form) return;
    setExporting(true);
    try {
      await downloadSubmissionsCSV(form.id, form.name, {
        search: searchQuery,
        processed,
      });
      toast.success("All matching submissions exported");
    } catch {
      toast.error("Unable to export submissions. Please retry.");
    } finally {
      setExporting(false);
    }
  };
  const columns: ResponsiveColumn<FormSubmission>[] = [
    {
      key: "submitted",
      header: "Submitted",
      mobileVariant: "header",
      render: (row) => (
        <Link onClick={() => handleViewDetails(row)}>
          {format(new Date(row.submitted_at), "MMM d, yyyy h:mm a")}
        </Link>
      ),
    },
    {
      key: "contact",
      header: "Contact",
      render: (row) =>
        row.contact ? (
          <Link onClick={() => navigate(`/crm/contacts/${row.contact}`)}>
            {row.contact_name || "View contact"}
          </Link>
        ) : (
          "No contact"
        ),
    },
    {
      key: "data",
      header: "Answers",
      mobileVariant: "body",
      render: (row) => (
        <Typography sx={{ overflowWrap: "anywhere" }}>
          {row.display_data
            ?.slice(0, 3)
            .map((item) => `${item.label}: ${String(item.value ?? "")}`)
            .join(" · ") || "No answers"}
        </Typography>
      ),
    },
    {
      key: "status",
      header: "Status",
      render: (row) => (
        <Chip color={row.processed ? "success" : "neutral"}>
          {row.processed ? "Processed" : "Unprocessed"}
        </Chip>
      ),
    },
  ];

  const handleViewDetails = (submission: FormSubmission) => {
    setSelectedSubmission(submission);
    setShowDetailsModal(true);
  };

  const handleMarkProcessed = (submission: FormSubmission) => {
    setSelectedSubmission(submission);
    setShowProcessModal(true);
  };

  const handleConfirmMarkProcessed = () => {
    if (selectedSubmission) {
      markProcessedMutation.mutate({
        id: selectedSubmission.id,
        notes: processingNotes,
      });
    }
  };

  const handleDelete = (submission: FormSubmission) => {
    if (confirm(`Are you sure you want to delete this submission?`)) {
      deleteSubmissionMutation.mutate(submission.id);
    }
  };

  if (formError)
    return (
      <Alert
        color="danger"
        endDecorator={<Button onClick={() => reloadForm()}>Retry</Button>}
      >
        Unable to load form details.
      </Alert>
    );

  if (!form) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography>Loading form details...</Typography>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        width: "100%",
        height: "100%",
        display: "flex",
        flexDirection: "column",
      }}
    >
      {/* Header */}
      <Box sx={{ p: 3, borderBottom: "1px solid", borderColor: "divider" }}>
        {/* Breadcrumbs */}
        <Breadcrumbs separator={<ChevronRightIcon />} sx={{ mb: 2 }}>
          <Link
            color="neutral"
            onClick={() => navigate("/marketing/forms")}
            sx={{ cursor: "pointer" }}
          >
            <HomeIcon style={{ marginRight: 4 }} />
            Forms
          </Link>
          <Typography>{form.name}</Typography>
          <Typography>Submissions</Typography>
        </Breadcrumbs>

        <Stack
          direction={{ xs: "column", md: "row" }}
          justifyContent="space-between"
          alignItems="center"
          sx={{ mb: 2 }}
        >
          <Stack direction="row" spacing={2} alignItems="center">
            <IconButton onClick={() => navigate("/marketing/forms")}>
              <BackIcon />
            </IconButton>
            <Box>
              <Typography level="h2">{form.name} - Submissions</Typography>
              <Typography level="body-sm" textColor="text.secondary">
                {submissionsData?.data.count || 0} matching submissions
              </Typography>
            </Box>
          </Stack>
          <Button
            startDecorator={<DownloadIcon />}
            onClick={handleExportCSV}
            loading={exporting}
            disabled={submissions.length === 0}
          >
            Export CSV
          </Button>
        </Stack>

        <CollapsibleFilters activeCount={Number(processed !== "all") + Number(Boolean(searchQuery))}><Select
          value={processed}
          onChange={(_, value) => setProcessed(value || "all")}
          sx={{ mb: 1, maxWidth: 400 }}
        >
          <Option value="all">All submissions</Option>
          <Option value="false">Unprocessed</Option>
          <Option value="true">Processed</Option>
        </Select>
        {/* Search */}
        <Input
          placeholder="Search submissions..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          startDecorator={<SearchIcon />}
          sx={{ maxWidth: 400 }}
        /></CollapsibleFilters>

      </Box>

      {/* Submissions Table */}
      <Box sx={{ flex: 1, overflow: "auto", p: 3 }}>
        <Sheet
          variant="outlined"
          sx={{ borderRadius: "sm", overflow: "hidden" }}
        >
          {isError ? (
            <Alert
              color="danger"
              endDecorator={<Button onClick={() => refetch()}>Retry</Button>}
            >
              Unable to load submissions.
            </Alert>
          ) : isLoading ? (
            <Typography sx={{ p: 3 }}>Loading submissions…</Typography>
          ) : (
            <ResponsiveTable
              rows={submissions}
              getRowKey={(row) => row.id}
              columns={columns}
              emptyState={
                <Typography>No submissions match these filters.</Typography>
              }
              rowActions={(submission) => (
                <Dropdown>
                  <MenuButton
                    slots={{ root: IconButton }}
                    aria-label="Submission actions"
                  >
                    <MoreIcon />
                  </MenuButton>
                  <Menu>
                    <MenuItem onClick={() => handleViewDetails(submission)}>
                      <ViewIcon />
                      View details
                    </MenuItem>
                    {!submission.processed && (
                      <MenuItem onClick={() => handleMarkProcessed(submission)}>
                        <ProcessedIcon />
                        Mark processed
                      </MenuItem>
                    )}
                    {submission.contact && (
                      <MenuItem
                        onClick={() =>
                          setOpportunityContactId(Number(submission.contact))
                        }
                      >
                        <OpportunityIcon />
                        Create opportunity
                      </MenuItem>
                    )}
                    <MenuItem
                      color="danger"
                      onClick={() => handleDelete(submission)}
                    >
                      <DeleteIcon />
                      Delete
                    </MenuItem>
                  </Menu>
                </Dropdown>
              )}
            />
          )}
          <Stack direction="row" spacing={2} sx={{ p: 2 }} alignItems="center">
            <Button
              disabled={page === 1 || isLoading}
              onClick={() => setPage(page - 1)}
            >
              Previous
            </Button>
            <Typography>Page {page}</Typography>
            <Button
              disabled={isLoading || !submissionsData?.data.next}
              onClick={() => setPage(page + 1)}
            >
              Next
            </Button>
          </Stack>
        </Sheet>
      </Box>

      {/* Submission Details Modal */}
      {selectedSubmission && (
        <Drawer
          open={showDetailsModal}
          anchor="right"
          onClose={() => setShowDetailsModal(false)}
          size="lg"
        >
          <Sheet sx={{ p: 3, overflow: "auto" }}>
            <ModalClose />
            <DialogTitle>Submission Details</DialogTitle>
            <DialogContent>
              <Stack spacing={2}>
                {/* Metadata */}
                <Card variant="soft">
                  <Typography level="title-sm" sx={{ mb: 1 }}>
                    Submission Information
                  </Typography>
                  <Stack spacing={1}>
                    <Typography level="body-sm">
                      <strong>Submitted:</strong>{" "}
                      {format(
                        new Date(selectedSubmission.submitted_at),
                        "MMMM d, yyyy h:mm a",
                      )}
                    </Typography>
                    <Typography level="body-sm">
                      <strong>Status:</strong>{" "}
                      {selectedSubmission.processed
                        ? "Processed"
                        : "Unprocessed"}
                    </Typography>
                    {selectedSubmission.contact_name && (
                      <Typography level="body-sm">
                        <strong>Contact:</strong>{" "}
                        {selectedSubmission.contact_name}
                      </Typography>
                    )}
                    {selectedSubmission.ip_address && (
                      <Typography level="body-sm">
                        <strong>IP Address:</strong>{" "}
                        {selectedSubmission.ip_address}
                      </Typography>
                    )}
                    {selectedSubmission.referrer_url && (
                      <Typography level="body-sm">
                        <strong>Referrer:</strong>{" "}
                        {selectedSubmission.referrer_url}
                      </Typography>
                    )}
                  </Stack>
                </Card>

                {/* Form Data */}
                <Card>
                  <Typography level="title-sm" sx={{ mb: 2 }}>
                    Form Data
                  </Typography>
                  <Stack spacing={1.5}>
                    {selectedSubmission.display_data?.map((item, index) => (
                      <Box key={index}>
                        <Typography level="body-sm" fontWeight="bold">
                          {item.label}
                        </Typography>
                        <Typography level="body-sm">
                          {item.value === null ||
                          item.value === undefined ||
                          item.value === "" ? (
                            <em>No value</em>
                          ) : (
                            String(item.value)
                          )}
                        </Typography>
                      </Box>
                    ))}
                  </Stack>
                </Card>

                {/* UTM Parameters */}
                {(selectedSubmission.utm_source ||
                  selectedSubmission.utm_medium ||
                  selectedSubmission.utm_campaign) && (
                  <Card variant="soft">
                    <Typography level="title-sm" sx={{ mb: 1 }}>
                      UTM Parameters
                    </Typography>
                    <Stack spacing={1}>
                      {selectedSubmission.utm_source && (
                        <Typography level="body-sm">
                          <strong>Source:</strong>{" "}
                          {selectedSubmission.utm_source}
                        </Typography>
                      )}
                      {selectedSubmission.utm_medium && (
                        <Typography level="body-sm">
                          <strong>Medium:</strong>{" "}
                          {selectedSubmission.utm_medium}
                        </Typography>
                      )}
                      {selectedSubmission.utm_campaign && (
                        <Typography level="body-sm">
                          <strong>Campaign:</strong>{" "}
                          {selectedSubmission.utm_campaign}
                        </Typography>
                      )}
                      {selectedSubmission.utm_term && (
                        <Typography level="body-sm">
                          <strong>Term:</strong> {selectedSubmission.utm_term}
                        </Typography>
                      )}
                      {selectedSubmission.utm_content && (
                        <Typography level="body-sm">
                          <strong>Content:</strong>{" "}
                          {selectedSubmission.utm_content}
                        </Typography>
                      )}
                    </Stack>
                  </Card>
                )}

                {/* Processing Notes */}
                {selectedSubmission.processing_notes && (
                  <Card variant="soft">
                    <Typography level="title-sm" sx={{ mb: 1 }}>
                      Processing Notes
                    </Typography>
                    <Typography level="body-sm">
                      {selectedSubmission.processing_notes}
                    </Typography>
                  </Card>
                )}
              </Stack>
            </DialogContent>
          </Sheet>
        </Drawer>
      )}

      {/* Mark as Processed Modal */}
      {selectedSubmission && (
        <Modal
          open={showProcessModal}
          onClose={() => setShowProcessModal(false)}
        >
          <ModalDialog>
            <ModalClose />
            <DialogTitle>Mark as Processed</DialogTitle>
            <DialogContent>
              <Stack spacing={2}>
                <Typography level="body-md">
                  Mark this submission as processed?
                </Typography>
                <FormControl>
                  <FormLabel>Notes (optional)</FormLabel>
                  <Textarea
                    value={processingNotes}
                    onChange={(e) => setProcessingNotes(e.target.value)}
                    placeholder="Add any notes about processing this submission..."
                    minRows={3}
                  />
                </FormControl>
                <Stack direction="row" spacing={1} justifyContent="flex-end">
                  <Button
                    variant="plain"
                    onClick={() => setShowProcessModal(false)}
                  >
                    Cancel
                  </Button>
                  <Button onClick={handleConfirmMarkProcessed}>
                    Mark as Processed
                  </Button>
                </Stack>
              </Stack>
            </DialogContent>
          </ModalDialog>
        </Modal>
      )}

      {opportunityContactId !== null && (
        <OpportunityModal
          open
          onClose={() => setOpportunityContactId(null)}
          contactId={opportunityContactId}
        />
      )}
    </Box>
  );
};
